<template>
    <div :data-part="part.id" class="editor-light-sentence" :style="listStyle">
        <Spinner v-if="loading" style="white-space: nowrap">{{ actionText }}</Spinner>
        <EditorLightSentenceControl
            :disabled="loading"
            :bookpart="part"
            @update:model-value="onUpdateSpeaker"
            @play="play"
        />

        <ContentEditableInput
            ref="input"
            v-model.trim="text"
            :disabled="loading"
            сlass="editor-light__sentence"
            @keyup.enter.prevent="onUpdateText"
            @blur="onInputBlur"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';

import debounce from 'lodash/debounce';

import EditorLightSentenceControl from '../EditorLightSentenceControl/EditorLightSentenceControl.vue';
import ContentEditableInput from '@speechki-frontend/shared/ui/ContentEditableInput/ContentEditableInput.vue';

import { getListIndentStyle, isListTextPart } from '../../utils';
import { updateAndGenerate } from '../../services/generation';

import { deleteMultipleBookparts } from '@speechki-frontend/core/book/api/books';

export default {
    name: 'Sentence',
    components: {
        EditorLightSentenceControl,
        ContentEditableInput,
    },

    props: {
        part: {
            type: Object,
            required: true,
        },
        playingPart: {
            type: Object,
            default: () => undefined,
        },
        noControl: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            actionText: '',
            text: this.part.ssml_text.replace(/<speak>|<\/speak>/g, ''),

            debouncedBlurHandler: undefined,
        };
    },

    computed: {
        isPlaying() {
            if (!!this.playingPart && this.part.id === this.playingPart.partId) {
                return true;
            }

            return false;
        },

        isListItem() {
            return isListTextPart(this.part);
        },

        listStyle() {
            if (!this.isListItem) return {};

            return {
                ...getListIndentStyle(this.part),
                display: 'block',
            };
        },
    },

    watch: {
        text(text) {
            if (!text.length) {
                this.deleteSentence();
            }
        },
    },

    mounted() {
        this.debouncedBlurHandler = debounce(this.onBlur, 250);
    },

    methods: {
        ...mapActions('pagedBookparts', ['fetchInitialBookparts']),

        onInputBlur(...args) {
            if (this.debouncedBlurHandler) this.debouncedBlurHandler(...args);
        },

        play() {
            this.$emit('playPart', { id: this.part.id });
        },

        onUpdateSpeaker(event) {
            this.updateSentence({
                speaker: event.id,
            });
        },

        onUpdateText(event = {}) {
            if (
                this.text.trim() === this.part.ssml_text.replace(/<speak>|<\/speak>/g, '').trim() ||
                event.shiftKey
            )
                return;

            if (event.preventDefault) {
                event.preventDefault();
            }

            this.updateSentence({
                ssml_text: `<speak>${this.text.trim().replace(/\n/g, ' ')}</speak>`,
            });
        },

        async updateSentence(data) {
            if (!this.part.permissions.generate) {
                this.$store.dispatch('pushToast', {
                    type: 'error',
                    title: `Text can't be edited`,
                    text: `You’ve reached the editing limit in this project. <br> To read more about how editing limits in projects work, please follow <a class='link' href='https://speechki.org/faq' target='_blank'>this link</a>`,
                    theme: 'big',
                });
                return;
            }

            try {
                this.loading = true;
                this.actionText = 'Generating Sentence..';

                await updateAndGenerate(this.part.id, data);
                this.fetchInitialBookparts({
                    page: this.$route.query.page,
                    preserveScroll: true,
                });
            } catch (e) {
                console.log(e);
                // TODO: add error handling of some sort
            } finally {
                this.loading = false;
            }
        },

        onEnter(event) {
            if (!event.shiftKey) {
                this.onUpdateText();
            }
        },

        async deleteSentence() {
            try {
                this.loading = true;
                this.actionText = 'Deleting Sentence..';

                await deleteMultipleBookparts(this.$route.params.id, this.part.id);

                setTimeout(() => {
                    this.fetchInitialBookparts({
                        page: this.$route.query.page,
                        preserveScroll: true,
                    });
                    this.loading = false;
                }, 1000);
            } catch (e) {
                console.log(e);
            }
        },

        onBlur() {
            if (!this.loading) {
                this.onUpdateText();
            }
        },
    },
};
</script>
